import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loansAPI, customersAPI, usersAPI, locationsAPI } from '../../services/api';
import { useCompany } from '../CompanyContext';
import { 
  Loan, 
  CreateLoanDto, 
  UpdateLoanDto, 
  LoanStatus, 
  PaymentFrequency, 
  Customer,
  User,
  Location 
} from '../../types';
import {
  ArrowLeftIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { toastSuccess, toastError } from '../../utils/toast';
import { useTranslation } from 'react-i18next';

// Form-specific interface that allows undefined for better UX
interface LoanFormData {
  companyId: string;
  locationId: string;
  borrowerId: string;
  loanOfficerId: string;
  approvedBy: string;
  principalAmount: number | undefined;
  monthlyInterestRate: number | undefined;
  lateFeeAmount: number | undefined;
  lateFeePercentage: number | undefined;
  gracePeriodDays: number;
  paymentFrequency: PaymentFrequency;
  installments: number;
  scheduledPaymentAmount: number | undefined;
  startDate: string;
  endDate: string;
  nextPaymentDue: string;
  terms: string;
  notes: string;
  internalNotes: string;
}

const LoanForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedCompany } = useCompany();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Options data
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  const [formData, setFormData] = useState<LoanFormData>({
    companyId: '',
    locationId: '',
    borrowerId: '',
    loanOfficerId: '',
    approvedBy: '',
    principalAmount: undefined,
    monthlyInterestRate: undefined,
    lateFeeAmount: undefined,
    lateFeePercentage: undefined,
    gracePeriodDays: 7,
    paymentFrequency: PaymentFrequency.MONTHLY,
    installments: 12,
    scheduledPaymentAmount: undefined,
    startDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    endDate: '',
    nextPaymentDue: '',
    terms: '',
    notes: '',
    internalNotes: '',
  });
  
  // Separate state for edit mode data that includes status
  const [editFormData, setEditFormData] = useState<UpdateLoanDto & { status?: LoanStatus }>({});

  // Calculate end date based on start date, installments, and payment frequency
  const calculateEndDate = (startDate: string, installments: number, frequency: PaymentFrequency): string => {
    if (!startDate || installments < 1) return '';
    
    const start = new Date(startDate);
    const endDate = new Date(start);
    
    // Calculate the end date by adding the payment periods
    const paymentsToAdd = installments - 1; // First payment is on start date
    
    switch (frequency) {
      case PaymentFrequency.WEEKLY:
        endDate.setDate(endDate.getDate() + (paymentsToAdd * 7));
        break;
      case PaymentFrequency.BIWEEKLY:
        endDate.setDate(endDate.getDate() + (paymentsToAdd * 14));
        break;
      case PaymentFrequency.MONTHLY:
        endDate.setMonth(endDate.getMonth() + paymentsToAdd);
        break;
      case PaymentFrequency.QUARTERLY:
        endDate.setMonth(endDate.getMonth() + (paymentsToAdd * 3));
        break;
      default:
        endDate.setMonth(endDate.getMonth() + paymentsToAdd);
    }
    
    return endDate.toISOString().split('T')[0];
  };

  // Calculate the end date whenever relevant fields change
  const getCalculatedEndDate = (): string => {
    return calculateEndDate(formData.startDate, formData.installments, formData.paymentFrequency);
  };

  // Calculate loan duration in months for display
  const getLoanDuration = (): number => {
    const installments = formData.installments || 0;
    const frequency = formData.paymentFrequency;
    
    switch (frequency) {
      case PaymentFrequency.WEEKLY:
        return installments / 4.345;
      case PaymentFrequency.BIWEEKLY:
        return (installments * 2) / 4.345;
      case PaymentFrequency.MONTHLY:
        return installments;
      case PaymentFrequency.QUARTERLY:
        return installments * 3;
      default:
        return installments;
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      setFormData(prev => ({
        ...prev,
        companyId: selectedCompany.id,
      }));
      
      loadOptions();
      
      if (id) {
        loadFormData();
      }
    }
  }, [selectedCompany, id]);

  // Auto-calculate scheduled payment amount when loan parameters change
  useEffect(() => {
    const principal = formData.principalAmount || 0;
    const monthlyRate = formData.monthlyInterestRate || 0;
    const installments = formData.installments || 0;
    const frequency = formData.paymentFrequency;
    
    // Only calculate if we have minimum required values
    if (principal > 0 && installments > 0) {
      const calculatedPayment = calculateScheduledPayment(principal, monthlyRate, installments, frequency);
      const roundedPayment = calculatedPayment > 0 ? Math.round(calculatedPayment * 100) / 100 : undefined;
      
      // Only update if the value actually changed to prevent unnecessary re-renders
      setFormData(prev => {
        if (prev.scheduledPaymentAmount !== roundedPayment) {
          return {
            ...prev,
            scheduledPaymentAmount: roundedPayment
          };
        }
        return prev;
      });
    } else {
      // Clear payment amount if insufficient data
      setFormData(prev => {
        if (prev.scheduledPaymentAmount !== undefined) {
          return {
            ...prev,
            scheduledPaymentAmount: undefined
          };
        }
        return prev;
      });
    }
  }, [formData.principalAmount, formData.monthlyInterestRate, formData.installments, formData.paymentFrequency]);

  const loadOptions = async () => {
    if (!selectedCompany) return;

    try {
      const [customersData, usersData, locationsData] = await Promise.all([
        customersAPI.getByCompany(selectedCompany.id),
        usersAPI.getByCompany(selectedCompany.id),
        locationsAPI.getByCompany(selectedCompany.id),
      ]);
      
      setCustomers(customersData);
      setUsers(usersData);
      setLocations(locationsData);
    } catch (err) {
      console.error('Error loading options:', err);
      toastError(t('loans.errorLoadingOptions'));
    }
  };

  const loadFormData = async () => {
    if (!id || !selectedCompany) return;

    try {
      setLoading(true);
      const loan = await loansAPI.getById(selectedCompany.id, id);
      
      setFormData({
        companyId: loan.companyId,
        locationId: loan.locationId,
        borrowerId: loan.borrowerId,
        loanOfficerId: loan.loanOfficerId,
        approvedBy: loan.approvedBy,
        principalAmount: loan.principalAmount,
        monthlyInterestRate: loan.monthlyInterestRate,
        lateFeeAmount: loan.lateFeeAmount || undefined,
        lateFeePercentage: loan.lateFeePercentage || undefined,
        gracePeriodDays: loan.gracePeriodDays || 7,
        paymentFrequency: loan.paymentFrequency,
        installments: loan.installments || 12,
        scheduledPaymentAmount: loan.scheduledPaymentAmount || undefined,
        startDate: new Date(loan.startDate).toISOString().split('T')[0],
        endDate: '', // Will be calculated, not loaded from existing data
        nextPaymentDue: loan.nextPaymentDue ? new Date(loan.nextPaymentDue).toISOString().split('T')[0] : '',
        terms: loan.terms || '',
        notes: loan.notes || '',
        internalNotes: loan.internalNotes || '',
      });
      
      // Set edit form data including status
      setEditFormData({
        status: loan.status,
      });
    } catch (err) {
      setError(t('loans.errorLoadingLoan'));
      console.error('Error loading loan:', err);
    } finally {
      setLoading(false);
    }
  };

  // Format currency for display
  const formatCurrencyInput = (value: number | undefined): string => {
    if (value === undefined || value === null) return '';
    return value.toString();
  };

  // Parse currency input back to number
  const parseCurrencyInput = (value: string): number | undefined => {
    if (value === '' || value === null) return undefined;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? undefined : parsed;
  };

  // Helper functions for percentage formatting and parsing (0-100 range for UI)
  const formatPercentageInput = (value: number | undefined): string => {
    if (value === undefined || value === null) return '';
    // Convert from decimal (0.05) to percentage (5) for display
    return (value * 100).toString();
  };

  const parsePercentageInput = (value: string): number | undefined => {
    if (value === '' || value === undefined) return undefined;
    const parsed = parseFloat(value);
    if (isNaN(parsed)) return undefined;
    // Convert from percentage (5) to decimal (0.05) for internal storage
    return parsed / 100;
  };

  // Calculate scheduled payment amount using loan payment formula with payment frequency
  const calculateScheduledPayment = (principal: number, monthlyRate: number, installments: number, frequency: PaymentFrequency): number => {
    if (!principal || principal <= 0 || !installments || installments <= 0) {
      return 0;
    }
    
    // Calculate total loan duration in months based on frequency and installments
    const getLoanDurationInMonths = (installments: number, frequency: PaymentFrequency): number => {
      switch (frequency) {
        case PaymentFrequency.WEEKLY:
          // installments weeks ÷ 4.345 weeks/month
          return installments / 4.345;
        case PaymentFrequency.BIWEEKLY:
          // installments bi-weeks × 2 weeks ÷ 4.345 weeks/month  
          return (installments * 2) / 4.345;
        case PaymentFrequency.MONTHLY:
          return installments;
        case PaymentFrequency.QUARTERLY:
          // installments quarters × 3 months/quarter
          return installments * 3;
        default:
          return installments;
      }
    };

    // Get the payment frequency rate (how often payments are made per month)
    const getPaymentFrequencyRate = (monthlyRate: number, frequency: PaymentFrequency): number => {
      if (!monthlyRate || monthlyRate <= 0) return 0;
      
      switch (frequency) {
        case PaymentFrequency.WEEKLY:
          // Weekly rate: approximately 4.345 payments per month
          return monthlyRate / 4.345;
        case PaymentFrequency.BIWEEKLY:
          // Bi-weekly rate: approximately 2.17 payments per month (26 payments/year ÷ 12 months)
          return monthlyRate / 2.17;
        case PaymentFrequency.MONTHLY:
          return monthlyRate;
        case PaymentFrequency.QUARTERLY:
          // Quarterly rate: compound monthly rate over 3 months
          return Math.pow(1 + monthlyRate, 3) - 1;
        default:
          return monthlyRate;
      }
    };
    
    const loanDurationInMonths = getLoanDurationInMonths(installments, frequency);
    const periodRate = getPaymentFrequencyRate(monthlyRate, frequency);
    
    // If no interest rate, simple division
    if (periodRate <= 0) {
      return principal / installments;
    }
    
    // Standard loan payment formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    // Where n = number of payments (installments), r = period rate
    const r = periodRate;
    const n = installments;
    const numerator = r * Math.pow(1 + r, n);
    const denominator = Math.pow(1 + r, n) - 1;
    
    return (principal * numerator) / denominator;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: any = value;
    
    // Handle currency inputs (scheduledPaymentAmount is calculated automatically)
    if (type === 'number' && ['principalAmount', 'lateFeeAmount'].includes(name)) {
      processedValue = parseCurrencyInput(value);
    } 
    // Handle interest rate input (0-100 percentage converted to decimal)
    else if (name === 'monthlyInterestRate') {
      processedValue = parsePercentageInput(value);
    }
    // Handle other percentage inputs (should allow empty but convert to undefined)
    else if (type === 'number' && ['lateFeePercentage'].includes(name)) {
      processedValue = value === '' ? undefined : parseFloat(value);
    }
    // Handle regular number inputs that shouldn't be undefined
    else if (type === 'number') {
      processedValue = value === '' ? 0 : parseFloat(value);
    }
    
    // Handle status changes for edit mode
    if (name === 'status' && id) {
      setEditFormData(prev => ({
        ...prev,
        [name]: processedValue,
      }));
    } else {
    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));
    }
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.locationId) {
      errors.locationId = t('loans.validation.locationRequired');
    }
    if (!formData.borrowerId) {
      errors.borrowerId = t('loans.validation.borrowerRequired');
    }
    if (!formData.loanOfficerId) {
      errors.loanOfficerId = t('loans.validation.loanOfficerRequired');
    }
    if (!formData.approvedBy) {
      errors.approvedBy = t('loans.validation.approverRequired');
    }
    if (!formData.principalAmount || formData.principalAmount <= 0) {
      errors.principalAmount = t('loans.validation.principalAmountRequired');
    }
    if (formData.monthlyInterestRate !== undefined && (formData.monthlyInterestRate < 0 || formData.monthlyInterestRate > 1)) {
      errors.monthlyInterestRate = t('loans.validation.interestRateInvalid');
    }
    if (formData.installments < 1) {
      errors.installments = t('loans.validation.installmentsRequired');
    }
    if (!formData.startDate) {
      errors.startDate = t('loans.validation.startDateRequired');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
    e.preventDefault();
    }
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Calculate end date before submission
      const calculatedEndDate = getCalculatedEndDate();

      if (id) {
        // Update existing loan - include status from edit form data
        const updateData: UpdateLoanDto = {
          ...formData,
          ...editFormData,
          principalAmount: formData.principalAmount || 0,
          monthlyInterestRate: formData.monthlyInterestRate || 0,
          lateFeeAmount: formData.lateFeeAmount || 0,
          lateFeePercentage: formData.lateFeePercentage || 0,
          scheduledPaymentAmount: formData.scheduledPaymentAmount || 0,
          startDate: formData.startDate,
          endDate: calculatedEndDate,
          nextPaymentDue: formData.nextPaymentDue || undefined,
        };
        await loansAPI.update(selectedCompany!.id, id, updateData);
        toastSuccess(t('loans.loanUpdated'));
      } else {
        // Create new loan - status will default to active on backend
        const createData: CreateLoanDto = {
          ...formData,
          principalAmount: formData.principalAmount || 0,
          monthlyInterestRate: formData.monthlyInterestRate || 0,
          lateFeeAmount: formData.lateFeeAmount || 0,
          lateFeePercentage: formData.lateFeePercentage || 0,
          scheduledPaymentAmount: formData.scheduledPaymentAmount || 0,
          startDate: formData.startDate,
          endDate: calculatedEndDate,
          nextPaymentDue: formData.nextPaymentDue || undefined,
        };
        await loansAPI.create(createData);
        toastSuccess(t('loans.loanCreated'));
      }

      navigate('/loans');
    } catch (err: any) {
      console.error('Error saving loan:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('loans.errorSavingLoan'));
      }
      toastError(t('loans.errorSavingLoan'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/loans');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={handleCancel}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-400" />
        </button>
        <div className="flex items-center space-x-3">
          <CreditCardIcon className="h-8 w-8 text-gray-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id ? t('loans.editLoan') : t('loans.newLoan')}
            </h1>
            <p className="text-sm text-gray-500">
              {id ? t('loans.editLoanSubtitle') : t('loans.newLoanSubtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-20">
        {/* Form Fields - Left Column */}
        <div className="lg:col-span-2">
          <form>
            <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
              <div className="px-4 py-6 sm:p-8">
                <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              
              {/* Location */}
              <div className="sm:col-span-3">
                <label htmlFor="locationId" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.location')} <span className="text-red-500">*</span>
                </label>
                <select
                  id="locationId"
                  name="locationId"
                  value={formData.locationId}
                  onChange={handleInputChange}
                  className={`mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    fieldErrors.locationId ? 'ring-red-300' : 'ring-gray-300'
                  } focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
                >
                  <option value="">{t('loans.form.selectLocation')}</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.locationId && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.locationId}</p>
                )}
              </div>

              {/* Borrower */}
              <div className="sm:col-span-3">
                <label htmlFor="borrowerId" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.borrower')} <span className="text-red-500">*</span>
                </label>
                <select
                  id="borrowerId"
                  name="borrowerId"
                  value={formData.borrowerId}
                  onChange={handleInputChange}
                  className={`mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    fieldErrors.borrowerId ? 'ring-red-300' : 'ring-gray-300'
                  } focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
                >
                  <option value="">{t('loans.form.selectBorrower')}</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.borrowerId && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.borrowerId}</p>
                )}
              </div>

              {/* Loan Officer */}
              <div className="sm:col-span-3">
                <label htmlFor="loanOfficerId" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.loanOfficer')} <span className="text-red-500">*</span>
                </label>
                <select
                  id="loanOfficerId"
                  name="loanOfficerId"
                  value={formData.loanOfficerId}
                  onChange={handleInputChange}
                  className={`mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    fieldErrors.loanOfficerId ? 'ring-red-300' : 'ring-gray-300'
                  } focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
                >
                  <option value="">{t('loans.form.selectLoanOfficer')}</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {fieldErrors.loanOfficerId && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.loanOfficerId}</p>
                )}
              </div>

              {/* Approver */}
              <div className="sm:col-span-3">
                <label htmlFor="approvedBy" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.approver')} <span className="text-red-500">*</span>
                </label>
                <select
                  id="approvedBy"
                  name="approvedBy"
                  value={formData.approvedBy}
                  onChange={handleInputChange}
                  className={`mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    fieldErrors.approvedBy ? 'ring-red-300' : 'ring-gray-300'
                  } focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
                >
                  <option value="">{t('loans.form.selectApprover')}</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {fieldErrors.approvedBy && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.approvedBy}</p>
                )}
              </div>

              {/* Principal Amount */}
              <div className="sm:col-span-2">
                <label htmlFor="principalAmount" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.principalAmount')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                <input
                  type="number"
                  id="principalAmount"
                  name="principalAmount"
                  step="0.01"
                  min="0"
                    value={formatCurrencyInput(formData.principalAmount)}
                  onChange={handleInputChange}
                    className={`mt-2 block w-full pl-7 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    fieldErrors.principalAmount ? 'ring-red-300' : 'ring-gray-300'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
                    placeholder=""
                />
                </div>
                {fieldErrors.principalAmount && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.principalAmount}</p>
                )}
              </div>

              {/* Monthly Interest Rate */}
              <div className="sm:col-span-2">
                <label htmlFor="monthlyInterestRate" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.monthlyInterestRate')}
                </label>
                <div className="relative">
                <input
                  type="number"
                  id="monthlyInterestRate"
                  name="monthlyInterestRate"
                    step="0.1"
                  min="0"
                    max="100"
                    value={formatPercentageInput(formData.monthlyInterestRate)}
                  onChange={handleInputChange}
                    className={`mt-2 block w-full pr-8 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    fieldErrors.monthlyInterestRate ? 'ring-red-300' : 'ring-gray-300'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
                  placeholder={t('loans.form.interestRatePlaceholder')}
                />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-gray-500 sm:text-sm">%</span>
                  </div>
                </div>
                {fieldErrors.monthlyInterestRate && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.monthlyInterestRate}</p>
                )}
              </div>

              {/* Payment Frequency */}
              <div className="sm:col-span-2">
                <label htmlFor="paymentFrequency" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.paymentFrequency')}
                </label>
                <select
                  id="paymentFrequency"
                  name="paymentFrequency"
                  value={formData.paymentFrequency}
                  onChange={handleInputChange}
                  className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                >
                  <option value={PaymentFrequency.WEEKLY}>{t('loans.paymentFrequency.WEEKLY')}</option>
                  <option value={PaymentFrequency.BIWEEKLY}>{t('loans.paymentFrequency.BIWEEKLY')}</option>
                  <option value={PaymentFrequency.MONTHLY}>{t('loans.paymentFrequency.MONTHLY')}</option>
                  <option value={PaymentFrequency.QUARTERLY}>{t('loans.paymentFrequency.QUARTERLY')}</option>
                </select>
              </div>

              {/* Installments */}
              <div className="sm:col-span-2">
                <label htmlFor="installments" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.installments')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="installments"
                  name="installments"
                  min="1"
                  step="1"
                  value={formData.installments}
                  onChange={handleInputChange}
                  className={`mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                    fieldErrors.installments ? 'ring-red-300' : 'ring-gray-300'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
                  placeholder={t('loans.form.installmentsPlaceholder')}
                />
                {fieldErrors.installments && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.installments}</p>
                )}
              </div>


              {/* Start Date */}
              <div className="sm:col-span-2">
                <label htmlFor="startDate" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.startDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
              {/* Calculated End Date Display */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.calculatedEndDate')}
                </label>
                <div className="mt-2 block w-full rounded-md border-0 py-1.5 px-3 text-gray-700 bg-gray-50 shadow-sm ring-1 ring-inset ring-gray-200 sm:text-sm sm:leading-6">
                  {getCalculatedEndDate() ? new Date(getCalculatedEndDate()).toLocaleDateString() : t('loans.form.endDateWillBeCalculated')}
              </div>
                <p className="mt-1 text-xs text-gray-500">
                  {t('loans.form.endDateCalculationNote')}
                </p>
              </div>


              {/* Status - Only show when editing */}
              {id && (
              <div className="sm:col-span-2">
                <label htmlFor="status" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.status')}
                </label>
                <select
                  id="status"
                  name="status"
                    value={editFormData.status || LoanStatus.ACTIVE}
                  onChange={handleInputChange}
                  className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                >
                  <option value={LoanStatus.ACTIVE}>{t('loans.status.ACTIVE')}</option>
                  <option value={LoanStatus.COMPLETED}>{t('loans.status.COMPLETED')}</option>
                  <option value={LoanStatus.DEFAULTED}>{t('loans.status.DEFAULTED')}</option>
                  <option value={LoanStatus.CANCELLED}>{t('loans.status.CANCELLED')}</option>
                </select>
              </div>
              )}

              {/* Scheduled Payment Amount (Calculated) */}
              <div className="sm:col-span-2">
                <label htmlFor="scheduledPaymentAmount" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.scheduledPaymentAmount')}
                  <span className="text-xs text-gray-500 ml-1">({t('loans.form.calculatedAutomatically')})</span>
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                <input
                  type="number"
                  id="scheduledPaymentAmount"
                  name="scheduledPaymentAmount"
                  step="0.01"
                  min="0"
                    value={formatCurrencyInput(formData.scheduledPaymentAmount)}
                    readOnly
                    className="mt-2 block w-full pl-7 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-gray-50 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder=""
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {t('loans.form.paymentCalculationNote')}
                </p>
              </div>

              {/* Terms */}
              <div className="sm:col-span-6">
                <label htmlFor="terms" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.terms')}
                </label>
                <textarea
                  id="terms"
                  name="terms"
                  rows={4}
                  value={formData.terms}
                  onChange={handleInputChange}
                  className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder={t('loans.form.termsPlaceholder')}
                />
              </div>

              {/* Notes */}
              <div className="sm:col-span-3">
                <label htmlFor="notes" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.notes')}
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={4}
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder={t('loans.form.notesPlaceholder')}
                />
              </div>

              {/* Internal Notes */}
              <div className="sm:col-span-3">
                <label htmlFor="internalNotes" className="block text-sm font-medium leading-6 text-gray-900">
                  {t('loans.form.internalNotes')}
                </label>
                <textarea
                  id="internalNotes"
                  name="internalNotes"
                  rows={4}
                  value={formData.internalNotes}
                  onChange={handleInputChange}
                  className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder={t('loans.form.internalNotesPlaceholder')}
                />
                </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Loan Summary - Right Column */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            {formData.principalAmount && formData.principalAmount > 0 ? (
              <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
                <div className="px-4 py-6 sm:p-6">
                  <h3 className="text-base font-semibold leading-7 text-gray-900 mb-4">
                    {t('loans.form.loanSummary')}
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Principal Amount */}
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">{t('loans.form.principalAmount')}:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        ${(formData.principalAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    
                    {/* Interest Rate */}
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">{t('loans.form.monthlyInterestRate')}:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {((formData.monthlyInterestRate || 0) * 100).toFixed(2)}% {t('loans.form.monthly')}
                      </span>
                    </div>
                    
                    {/* Payment Frequency */}
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">{t('loans.form.paymentFrequency')}:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {t(`loans.paymentFrequency.${formData.paymentFrequency.toUpperCase()}`)}
                      </span>
                    </div>
                    
                    {/* Number of Installments */}
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">{t('loans.form.installments')}:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {formData.installments || 0} {t('loans.form.payments')}
                      </span>
                    </div>
                    
                    {/* Loan Duration */}
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">{t('loans.form.loanDuration')}:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {getLoanDuration().toFixed(1)} {t('loans.form.months')}
                      </span>
                    </div>

                    <hr className="border-gray-200" />
                    
                    {/* Payment Amount */}
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">{t('loans.form.paymentAmount')}:</span>
                      <span className="text-sm font-bold text-blue-600">
                        ${(formData.scheduledPaymentAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    
                    {/* Total Amount */}
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">{t('loans.form.totalAmount')}:</span>
                      <span className="text-sm font-bold text-green-600">
                        ${((formData.scheduledPaymentAmount || 0) * (formData.installments || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    
                    {/* Total Interest */}
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">{t('loans.form.totalInterest')}:</span>
                      <span className="text-sm font-semibold text-orange-600">
                        ${Math.max(0, ((formData.scheduledPaymentAmount || 0) * (formData.installments || 0)) - (formData.principalAmount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
          </div>

                    {/* End Date */}
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">{t('loans.form.endDate')}:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {getCalculatedEndDate() ? new Date(getCalculatedEndDate()).toLocaleDateString() : '-'}
                      </span>
          </div>

                    {/* Summary Note */}
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 text-center">
                        {t('loans.form.summaryNote')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="text-center">
                  <div className="text-gray-400 mb-2">
                    <svg className="mx-auto h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500">
                    {t('loans.form.enterAmountToSeeSummary')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Form Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-900/10 px-4 py-4 sm:px-8 z-10">
        <div className="flex items-center justify-end gap-x-6 max-w-7xl mx-auto">
            <button
              type="button"
              onClick={handleCancel}
              className="text-sm font-semibold leading-6 text-gray-900"
              disabled={saving}
            >
              {t('actions.cancel')}
            </button>
            <button
            type="button"
            onClick={handleSubmit}
              disabled={saving}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('actions.saving') : (id ? t('actions.update') : t('actions.create'))}
            </button>
          </div>
        </div>
    </div>
  );
};

export default LoanForm;
